import { WSList } from "@wingspanhq/fe-component-library";

import { AIValidationWidget } from "../../../../components/AIValidationWidget";
import {
  RequirementContextType,
  RequirementTableCallback
} from "../../../../types";
import { WayfinderWidget } from "../../../../../../shared/components/WayfinderWidget";
import {
  SharedFileRequestContextType,
  SharedFileRequestJobType,
  SharedFileRequestStatus
} from "@wingspanhq/shared-files/dist/lib/interfaces";
import { useQuerySharedFile } from "../../../../../../query/sharedFile/query/useQuerySharedFile";
import { useEffect } from "react";
import { WSQueries } from "../../../../../../query/WSQuery";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces/api/requirement";
import { SHARED_FILE_REQUIREMENT_EVENT } from "../../configs/utils";
import { AssurelyWidget } from "../AssurelyWidget";
import { selectorIsRequirementReadyToDownloadFile } from "../../../../selectors/selectorIsRequirementReadyToDownloadFile";
import { getRequirementDefinitionIsWayfinder } from "../../../../../../modules/RequirementDefinitions/utils";

type Props = {
  sharedFileRequestId: string;
  contextType: RequirementContextType;
  onSuccess: RequirementTableCallback;
  requirement: IRequirementResponse;
};

export const SharedFileDetailsWidgetsContainer: React.FC<Props> = ({
  sharedFileRequestId,
  contextType,
  requirement,
  onSuccess
}) => {
  const querySharedFilesRequest = useQuerySharedFile(sharedFileRequestId);
  const sharedFileRequestData = querySharedFilesRequest.data;
  const isWayfinder = getRequirementDefinitionIsWayfinder({
    type: requirement.requirementType,
    contextType: sharedFileRequestData?.contextType
  });

  useEffect(() => {
    // Refetch the shared file request if the status is Processing
    if (sharedFileRequestData?.status === SharedFileRequestStatus.Processing) {
      // Refetch the shared file request for every 5 seconds
      const interval = setInterval(() => {
        // This is to ensure that the status is updated real-time in the UI
        querySharedFilesRequest.refetch().then(result => {
          if (result.data?.status === SharedFileRequestStatus.Complete) {
            clearInterval(interval);
            onSuccess(
              SHARED_FILE_REQUIREMENT_EVENT.ValidationSuccess,
              requirement
            );
          }
        });
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [sharedFileRequestData?.status, querySharedFilesRequest]);

  return (
    <WSQueries queries={{ querySharedFilesRequest }}>
      {({ querySharedFilesRequestData: sharedFileRequest }) => {
        // Do not show any widgets for SharedFile requirement type if contextType is None
        if (
          sharedFileRequest.contextType === SharedFileRequestContextType.None
        ) {
          return null;
        }

        // Filter out validation jobs from the shared file request
        const validationJobs = sharedFileRequest.jobs.filter(
          job => job.type === SharedFileRequestJobType.Validation
        );
        return (
          <WSList gap="L">
            {/* Show AssurelyWidget widget only for payees */}
            {!isWayfinder &&
            contextType === RequirementContextType.Payer &&
            !selectorIsRequirementReadyToDownloadFile(requirement) ? (
              <AssurelyWidget />
            ) : null}
            <AIValidationWidget
              contextType={contextType}
              validationItems={validationJobs}
            />
            {/* Show WayFinder widget only for payers */}
            {contextType === RequirementContextType.Payee && (
              <WayfinderWidget />
            )}
          </WSList>
        );
      }}
    </WSQueries>
  );
};
